/**
 * GroupedResultsTable Component
 * Displays power records grouped by Report with expandable rows
 * Desktop: Traditional pagination
 * Mobile: Infinite scroll with automatic loading
 */

import { useState, useEffect, useRef, useMemo } from 'react';
import {
  ChevronDownIcon,
  ChevronRightIcon as ChevronCollapsedIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';
import { OathSpinner } from '../../../shared/OathUI';
import {
  OathCheckbox,
  OathSortableHeader,
  OathPagination,
  OathSelectionBanner,
  formatCurrency,
  formatDate,
  getPowerStatusColor,
} from '../../../shared/OathTableComponents';
import { SORT_FIELDS } from '../../../../services/oathPowerInquiryService';
import { getDueDate, getDueDateHighlight } from '../utils/dueDate';

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200, 500, 1000];

// Tailwind lg breakpoint
const LG_BREAKPOINT = 1024;

/**
 * Power row within expanded report
 */
const PowerRow = ({ power, onViewDetails, isSelected, onSelectionChange }) => {
  const isUnexecuted = power.powerStatus === 'U';
  const amount = isUnexecuted ? power.powerAmount : power.bondAmount;
  const due = getDueDate(power);
  const highlight = getDueDateHighlight(due);

  return (
    <tr className={`hover:bg-blue-50/50 dark:hover:bg-blue-900/10 transition-colors ${isSelected ? 'bg-primary-50/70 dark:bg-primary-900/20' : ''}`}>
      <td className="px-4 py-2 pl-12 w-10">
        <OathCheckbox
          checked={isSelected}
          onChange={(checked) => onSelectionChange(power.powerNo, checked)}
        />
      </td>
      <td className="px-4 py-2">
        <button
          onClick={() => onViewDetails(power.powerNo)}
          className="inline-flex items-center gap-1.5 font-mono text-sm font-medium text-primary dark:text-primary-light hover:underline"
          title="View details"
        >
          <EyeIcon className="h-4 w-4" />
          {power.powerNo}
        </button>
      </td>
      <td className="px-4 py-2 text-center">
        <span className={`inline-flex items-center justify-center px-2 py-0.5 text-xs font-bold rounded-full ${getPowerStatusColor(power.powerStatus)}`}>
          {power.powerStatusDescription || power.powerStatus}
        </span>
      </td>
      <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300">
        {power.defendantLastName || '-'}{power.defendantFirstName ? `, ${power.defendantFirstName}` : ''}
      </td>
      <td className="px-4 py-2 text-right text-sm font-medium text-gray-900 dark:text-white">
        {formatCurrency(amount)}
      </td>
      <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400">
        {formatDate(power.executionDate)}
      </td>
      <td className={`px-4 py-2 text-sm ${highlight?.cell || ''}`}>
        <span className={highlight?.text || 'text-gray-600 dark:text-gray-400'}>
          {formatDate(due)}
        </span>
      </td>
    </tr>
  );
};

/**
 * Expandable report row
 */
const ReportRow = ({
  report,
  isExpanded,
  isLoading,
  powers,
  onToggle,
  onLoadPowers,
  onViewPowerDetails,
  selectedPowers,
  onSelectionChange,
}) => {
  // Load powers when expanded and not cached
  useEffect(() => {
    if (isExpanded && !powers && !isLoading) {
      onLoadPowers(report.reportNo);
    }
  }, [isExpanded, powers, isLoading, report.reportNo, onLoadPowers]);

  return (
    <>
      {/* Report Header Row */}
      <tr
        className={`cursor-pointer transition-colors ${
          isExpanded
            ? 'bg-primary-50 dark:bg-primary-900/20'
            : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
        }`}
        onClick={onToggle}
      >
        {/* Expand/Collapse Icon */}
        <td className="px-4 py-3 w-10">
          <div className="flex items-center justify-center">
            {isExpanded ? (
              <ChevronDownIcon className="h-5 w-5 text-primary dark:text-primary-light" />
            ) : (
              <ChevronCollapsedIcon className="h-5 w-5 text-gray-400" />
            )}
          </div>
        </td>

        {/* Report Number */}
        <td className="px-4 py-3">
          <span className="font-mono font-bold text-gray-900 dark:text-white">
            {report.reportNo || '-'}
          </span>
        </td>

        {/* Batch ID */}
        <td className="px-4 py-3">
          <span className="text-sm text-gray-600 dark:text-gray-400 font-mono">
            {report.batchId || '-'}
          </span>
        </td>

        {/* Agency */}
        <td className="px-4 py-3">
          <span className="text-sm text-gray-700 dark:text-gray-300">
            {report.agencyId || '-'}
          </span>
        </td>

        {/* Power Count */}
        <td className="px-4 py-3 text-center">
          <span className="inline-flex items-center justify-center px-2.5 py-1 text-xs font-bold rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">
            {report.powerCount || 0} powers
          </span>
        </td>

        {/* Open Liability */}
        <td className="px-4 py-3 text-right">
          <span className="text-sm font-bold text-gray-900 dark:text-white">
            {formatCurrency(report.openLiability)}
          </span>
        </td>

        {/* Report Date */}
        <td className="px-4 py-3">
          <span className="text-sm text-gray-600 dark:text-gray-400">
            {formatDate(report.reportingDate)}
          </span>
        </td>
      </tr>

      {/* Expanded Powers Section */}
      {isExpanded && (
        <tr>
          <td colSpan={7} className="p-0">
            <div className="bg-gray-50 dark:bg-gray-900/50 border-y border-gray-200 dark:border-gray-700">
              {isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <OathSpinner size="md" />
                  <span className="ml-3 text-sm text-gray-500 dark:text-gray-400">Loading powers...</span>
                </div>
              ) : powers && powers.length > 0 ? (
                <>
                  <table className="min-w-full">
                    <thead>
                      <tr className="bg-gray-100 dark:bg-gray-800">
                        <th className="px-4 py-2 pl-12 w-10"></th>
                        <th className="px-4 py-2 text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase">Power #</th>
                        <th className="px-4 py-2 text-center text-xs font-bold text-gray-500 dark:text-gray-400 uppercase">Status</th>
                        <th className="px-4 py-2 text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase">Defendant</th>
                        <th className="px-4 py-2 text-right text-xs font-bold text-gray-500 dark:text-gray-400 uppercase">Amount</th>
                        <th className="px-4 py-2 text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase">Exec Date</th>
                        <th className="px-4 py-2 text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase">Due Date</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
                      {powers.map((power, idx) => (
                        <PowerRow
                          key={power.powerNo || idx}
                          power={power}
                          onViewDetails={onViewPowerDetails}
                          isSelected={selectedPowers?.has(power.powerNo)}
                          onSelectionChange={onSelectionChange}
                        />
                      ))}
                    </tbody>
                  </table>
                  {/* Summary Footer */}
                  <div className="px-4 py-3 bg-gray-100 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      {powers.length} power{powers.length !== 1 ? 's' : ''} matching filters
                    </span>
                    <span className="text-sm font-bold text-gray-900 dark:text-white">
                      Total: {formatCurrency(powers.reduce((sum, p) => {
                        const isUnexecuted = p.powerStatus === 'U';
                        const amount = isUnexecuted ? (p.powerAmount || 0) : (p.bondAmount || 0);
                        return sum + amount;
                      }, 0))}
                    </span>
                  </div>
                </>
              ) : (
                <div className="py-6 text-center text-sm text-gray-500 dark:text-gray-400">
                  No powers found in this report
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

/**
 * Mobile card view for a report group
 */
const ReportCard = ({
  report,
  isExpanded,
  isLoading,
  powers,
  onToggle,
  onLoadPowers,
  onViewPowerDetails,
  selectedPowers,
  onSelectionChange,
}) => {
  // Load powers when expanded and not cached
  useEffect(() => {
    if (isExpanded && !powers && !isLoading) {
      onLoadPowers(report.reportNo);
    }
  }, [isExpanded, powers, isLoading, report.reportNo, onLoadPowers]);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden shadow-sm">
      {/* Card Header - Clickable */}
      <button
        type="button"
        onClick={onToggle}
        className={`w-full px-4 py-3 flex items-center justify-between transition-colors ${
          isExpanded ? 'bg-primary-50 dark:bg-primary-900/20' : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
        }`}
      >
        <div className="flex items-center gap-3">
          {isExpanded ? (
            <ChevronDownIcon className="h-5 w-5 text-primary dark:text-primary-light flex-shrink-0" />
          ) : (
            <ChevronCollapsedIcon className="h-5 w-5 text-gray-400 flex-shrink-0" />
          )}
          <div className="text-left">
            <div className="font-mono font-bold text-gray-900 dark:text-white">
              Report {report.reportNo || '-'}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              Batch #{report.batchId} &bull; Agency {report.agencyId}
            </div>
          </div>
        </div>
        <div className="text-right">
          <div className="text-sm font-bold text-gray-900 dark:text-white">
            {formatCurrency(report.openLiability)}
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            {report.powerCount} powers
          </div>
        </div>
      </button>

      {/* Expanded Powers */}
      {isExpanded && (
        <div className="border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
          {isLoading ? (
            <div className="flex items-center justify-center py-6">
              <OathSpinner size="sm" />
              <span className="ml-2 text-sm text-gray-500">Loading...</span>
            </div>
          ) : powers && powers.length > 0 ? (
            <>
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {powers.map((power, idx) => {
                  const isUnexecuted = power.powerStatus === 'U';
                  const amount = isUnexecuted ? power.powerAmount : power.bondAmount;
                  const isSelected = selectedPowers?.has(power.powerNo);
                  const due = getDueDate(power);
                  const highlight = getDueDateHighlight(due);

                  return (
                    <div key={power.powerNo || idx} className={`p-3 hover:bg-white dark:hover:bg-gray-800 ${isSelected ? 'bg-primary-50/50 dark:bg-primary-900/10' : ''}`}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <OathCheckbox
                            checked={isSelected}
                            onChange={(checked) => onSelectionChange && onSelectionChange(power.powerNo, checked)}
                          />
                          <button
                            onClick={() => onViewPowerDetails(power.powerNo)}
                            className="font-mono text-sm font-medium text-primary dark:text-primary-light hover:underline"
                          >
                            {power.powerNo}
                          </button>
                        </div>
                        <span className={`px-2 py-0.5 text-xs font-bold rounded-full ${getPowerStatusColor(power.powerStatus)}`}>
                          {power.powerStatusDescription || power.powerStatus}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-sm pl-6">
                        <span className="text-gray-600 dark:text-gray-400">
                          {power.defendantLastName || '-'}{power.defendantFirstName ? `, ${power.defendantFirstName}` : ''}
                        </span>
                        <span className="font-medium text-gray-900 dark:text-white">
                          {formatCurrency(amount)}
                        </span>
                      </div>
                      {highlight && (
                        <div className="mt-1 pl-6 text-xs">
                          <span className="text-gray-500 dark:text-gray-400">Due: </span>
                          <span className={`inline-block px-1.5 py-0.5 rounded ${highlight.cell} ${highlight.text || 'text-gray-900 dark:text-white'}`}>
                            {formatDate(due)}
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              {/* Summary Footer */}
              <div className="px-3 py-2 bg-gray-100 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
                <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                  {powers.length} power{powers.length !== 1 ? 's' : ''}
                </span>
                <span className="text-sm font-bold text-gray-900 dark:text-white">
                  Total: {formatCurrency(powers.reduce((sum, p) => {
                    const isUnexecuted = p.powerStatus === 'U';
                    const amount = isUnexecuted ? (p.powerAmount || 0) : (p.bondAmount || 0);
                    return sum + amount;
                  }, 0))}
                </span>
              </div>
            </>
          ) : (
            <div className="py-4 text-center text-sm text-gray-500">No powers found</div>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Grouped Results Table component
 */
const GroupedResultsTable = ({
  reportGroups = [],
  totalReports = 0,
  totalPowers = 0,
  pageNumber = 1,
  pageSize = 25,
  totalPages = 0,
  onPageChange,
  onPageSizeChange,
  expandedReports = new Set(),
  loadingReportPowers = new Set(),
  reportPowersCache = {},
  onToggleExpanded,
  onLoadReportPowers,
  onViewPowerDetails,
  isLoading = false,
  hasSearched = false,
  error = null,
  enableFixedHeaderFooter = true,
  // Sorting
  sortBy,
  sortDescending,
  onSortChange,
  // Selection
  selectedPowers = new Set(),
  onSelectionChange,
  selectAllMatching = false,
  onSelectAllMatching,
  onBulkDischarge,
  onAddToDischargeQueue,
  onBulkComment,
}) => {
  const tableContainerRef = useRef(null);
  const theadRef = useRef(null);
  const paginationRef = useRef(null);
  const sentinelRef = useRef(null);

  // Fixed header/footer state (desktop only)
  const [isHeaderFixed, setIsHeaderFixed] = useState(false);
  const [fixedHeaderStyle, setFixedHeaderStyle] = useState({});
  const [columnWidths, setColumnWidths] = useState([]);
  const [isFooterFixed, setIsFooterFixed] = useState(false);
  const [fixedFooterStyle, setFixedFooterStyle] = useState({});
  const prevPaginationRef = useRef({ pageNumber, pageSize });

  // Mobile detection and infinite scroll state
  const [isMobile, setIsMobile] = useState(false);
  const [accumulatedReports, setAccumulatedReports] = useState([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const lastSearchKeyRef = useRef(null);

  // Detect mobile viewport
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < LG_BREAKPOINT);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Scroll to top when page or page size changes (desktop only)
  useEffect(() => {
    const prev = prevPaginationRef.current;
    const hasChanged = prev.pageNumber !== pageNumber || prev.pageSize !== pageSize;

    if (hasChanged && hasSearched && !isMobile) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    prevPaginationRef.current = { pageNumber, pageSize };
  }, [pageNumber, pageSize, hasSearched, isMobile]);

  // Track when table header scrolls out of view to make it fixed (desktop only)
  const hasResults = reportGroups.length > 0;
  useEffect(() => {
    const thead = theadRef.current;
    const container = tableContainerRef.current;
    if (!thead || !container) return;

    const isDesktop = () => window.innerWidth >= LG_BREAKPOINT;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!isDesktop()) {
          setIsHeaderFixed(false);
          return;
        }

        const isOutOfView = !entry.isIntersecting;
        setIsHeaderFixed(isOutOfView);

        if (isOutOfView) {
          const containerRect = container.getBoundingClientRect();
          const thCells = thead.querySelectorAll('th');
          const widths = Array.from(thCells).map(th => th.getBoundingClientRect().width);
          setColumnWidths(widths);
          setFixedHeaderStyle({
            position: 'fixed',
            top: '3.5rem', // Below nav bar (56px)
            left: containerRect.left,
            width: containerRect.width,
            zIndex: 20,
          });
        }
      },
      { threshold: 0, rootMargin: '-56px 0px 0px 0px' }
    );

    const handleResize = () => {
      if (!isDesktop()) {
        setIsHeaderFixed(false);
      }
    };

    observer.observe(thead);
    window.addEventListener('resize', handleResize);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', handleResize);
    };
  }, [hasResults]);

  // Track when pagination footer is not visible to show fixed footer (desktop only)
  useEffect(() => {
    const pagination = paginationRef.current;
    const container = tableContainerRef.current;
    if (!pagination || !container) return;

    const isDesktop = () => window.innerWidth >= LG_BREAKPOINT;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!isDesktop()) {
          setIsFooterFixed(false);
          return;
        }

        const isVisible = entry.isIntersecting;
        setIsFooterFixed(!isVisible);

        if (!isVisible) {
          const containerRect = container.getBoundingClientRect();
          setFixedFooterStyle({
            position: 'fixed',
            bottom: '0',
            left: containerRect.left,
            width: containerRect.width,
            zIndex: 20,
          });
        }
      },
      { threshold: 0 }
    );

    const handleResize = () => {
      if (!isDesktop()) {
        setIsFooterFixed(false);
      }
    };

    observer.observe(pagination);
    window.addEventListener('resize', handleResize);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', handleResize);
    };
  }, [hasResults]);

  // Generate a key to detect new searches vs page changes
  // When totalReports changes or pageNumber resets to 1, it's a new search
  const searchKey = useMemo(() => {
    return `${totalReports}-${hasSearched}`;
  }, [totalReports, hasSearched]);

  // Handle accumulation for infinite scroll
  useEffect(() => {
    if (!isMobile) {
      // On desktop, just use reportGroups directly
      return;
    }

    if (pageNumber === 1 || searchKey !== lastSearchKeyRef.current) {
      // New search - replace accumulated reports
      setAccumulatedReports(reportGroups);
      lastSearchKeyRef.current = searchKey;
    } else if (reportGroups.length > 0) {
      // Subsequent page - append to accumulated reports
      setAccumulatedReports(prev => {
        // Avoid duplicates by checking reportNo
        const existingIds = new Set(prev.map(r => r.reportNo));
        const newReports = reportGroups.filter(r => !existingIds.has(r.reportNo));
        return [...prev, ...newReports];
      });
    }
    setIsLoadingMore(false);
  }, [reportGroups, pageNumber, isMobile, searchKey]);

  // Reset accumulated reports when switching to mobile
  useEffect(() => {
    if (isMobile && reportGroups.length > 0) {
      setAccumulatedReports(reportGroups);
    }
  }, [isMobile]);

  // IntersectionObserver for infinite scroll on mobile
  useEffect(() => {
    if (!isMobile || !sentinelRef.current || isLoading || isLoadingMore) return;
    if (pageNumber >= totalPages) return; // No more pages

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && !isLoading && !isLoadingMore && pageNumber < totalPages) {
          setIsLoadingMore(true);
          onPageChange(pageNumber + 1);
        }
      },
      {
        threshold: 0.1,
        rootMargin: '200px' // Start loading before reaching the end
      }
    );

    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [isMobile, isLoading, isLoadingMore, pageNumber, totalPages, onPageChange]);

  // Determine which reports to display
  const displayReports = isMobile ? accumulatedReports : reportGroups;

  // Loading state
  if (isLoading && !displayReports.length) {
    return (
      <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-12 overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary/60 via-primary to-primary/60" />
        <div className="flex flex-col items-center justify-center">
          <div className="p-4 bg-primary/10 rounded-full">
            <OathSpinner size="lg" className="text-primary" />
          </div>
          <p className="mt-4 text-base font-medium text-gray-700 dark:text-gray-300">Searching reports...</p>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">This may take a moment</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border-2 border-red-200 dark:border-red-900/50 p-8">
        <div className="flex flex-col items-center text-center">
          <div className="p-3 bg-red-100 dark:bg-red-900/30 rounded-full">
            <ExclamationTriangleIcon className="h-8 w-8 text-red-600 dark:text-red-400" />
          </div>
          <h3 className="mt-4 text-lg font-semibold text-gray-900 dark:text-white">Search Error</h3>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400 max-w-md">{error}</p>
        </div>
      </div>
    );
  }

  // Not searched yet
  if (!hasSearched) {
    return (
      <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-dashed border-gray-300 dark:border-gray-600 p-12 overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary/60 via-primary to-primary/60" />
        <div className="flex flex-col items-center text-center">
          <div className="p-4 bg-gray-100 dark:bg-gray-700 rounded-full">
            <MagnifyingGlassIcon className="h-10 w-10 text-gray-400 dark:text-gray-500" />
          </div>
          <h3 className="mt-5 text-lg font-semibold text-gray-900 dark:text-white">Ready to Search</h3>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400 max-w-sm">
            Select your agencies, enter search criteria, and click Search to find reports.
          </p>
        </div>
      </div>
    );
  }

  // No results
  if (displayReports.length === 0) {
    return (
      <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-12 overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary/60 via-primary to-primary/60" />
        <div className="flex flex-col items-center text-center">
          <div className="p-4 bg-amber-100 dark:bg-amber-900/30 rounded-full">
            <DocumentTextIcon className="h-10 w-10 text-amber-600 dark:text-amber-400" />
          </div>
          <h3 className="mt-5 text-lg font-semibold text-gray-900 dark:text-white">No Reports Found</h3>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400 max-w-sm">
            No reports matched your search criteria. Try adjusting your filters or search terms.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Loading overlay for subsequent searches (desktop only - mobile uses bottom spinner) */}
      {isLoading && !isLoadingMore && displayReports.length > 0 && (
        <div className="absolute inset-0 bg-white/70 dark:bg-gray-800/70 flex items-center justify-center z-10 backdrop-blur-sm rounded-xl">
          <div className="flex flex-col items-center">
            <OathSpinner size="lg" />
            <p className="mt-3 text-sm font-medium text-gray-600 dark:text-gray-400">Updating results...</p>
          </div>
        </div>
      )}

      {/* Fixed Header - appears when scrolling past original header (desktop only) */}
      {enableFixedHeaderFooter && isHeaderFixed && (
        <div style={fixedHeaderStyle} className="relative bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
          {/* Top accent bar */}
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary/60 via-primary to-primary/60 z-10" />
          <table className="min-w-full">
            <thead>
              <tr>
                <th style={{ width: columnWidths[0] }} className="px-4 py-4 w-10"></th>
                <th style={{ width: columnWidths[1] }} className="px-4 py-4 text-left">
                  <OathSortableHeader label="Report #" field={SORT_FIELDS.REPORT_NUMBER} sortField={sortBy} sortDescending={sortDescending} onSort={onSortChange} />
                </th>
                <th style={{ width: columnWidths[2] }} className="px-4 py-4 text-left">
                  <OathSortableHeader label="Batch ID" field={SORT_FIELDS.BATCH_ID} sortField={sortBy} sortDescending={sortDescending} onSort={onSortChange} />
                </th>
                <th style={{ width: columnWidths[3] }} className="px-4 py-4 text-left">
                  <OathSortableHeader label="Agency" field={SORT_FIELDS.AGENCY_ID} sortField={sortBy} sortDescending={sortDescending} onSort={onSortChange} />
                </th>
                <th style={{ width: columnWidths[4] }} className="px-4 py-4 text-center">
                  <OathSortableHeader label="Powers" field={SORT_FIELDS.POWER_COUNT} sortField={sortBy} sortDescending={sortDescending} onSort={onSortChange} align="center" />
                </th>
                <th style={{ width: columnWidths[5] }} className="px-4 py-4 text-right">
                  <OathSortableHeader label="Open Liability" field={SORT_FIELDS.BOND_AMOUNT} sortField={sortBy} sortDescending={sortDescending} onSort={onSortChange} align="right" />
                </th>
                <th style={{ width: columnWidths[6] }} className="px-4 py-4 text-left">
                  <OathSortableHeader label="Report Date" field={SORT_FIELDS.REPORT_DATE} sortField={sortBy} sortDescending={sortDescending} onSort={onSortChange} />
                </th>
              </tr>
            </thead>
          </table>
        </div>
      )}

      {/* Desktop Table */}
      <div ref={tableContainerRef} className="relative hidden lg:block overflow-x-auto bg-white dark:bg-gray-800 rounded-t-xl shadow-sm border border-gray-200 dark:border-gray-700">
        {/* Top accent bar */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary/60 via-primary to-primary/60 z-10" />
        {/* Selection banner */}
        <OathSelectionBanner
          selectedCount={selectedPowers.size}
          totalCount={totalPowers}
          selectAllMatching={selectAllMatching}
          onSelectAllMatching={onSelectAllMatching}
          onClearSelection={() => onSelectAllMatching && onSelectAllMatching(false)}
        >
          {onBulkDischarge && (
            <button
              onClick={onBulkDischarge}
              className="ml-1 px-3 py-1.5 qhd:px-4 qhd:py-2 bg-primary text-white text-sm qhd:text-base font-medium rounded-lg hover:bg-primary-dark active:scale-95 transition-all shadow-sm"
            >
              Discharge Now
            </button>
          )}
          {onAddToDischargeQueue && (
            <button
              onClick={onAddToDischargeQueue}
              className="ml-1 px-3 py-1.5 qhd:px-4 qhd:py-2 bg-white dark:bg-gray-800 text-primary dark:text-primary-light text-sm qhd:text-base font-medium rounded-lg border border-primary/40 dark:border-primary-light/40 hover:bg-primary-50 dark:hover:bg-primary-900/30 active:scale-95 transition-all shadow-sm"
            >
              Add to Discharge Queue
            </button>
          )}
          {onBulkComment && (
            <button
              onClick={onBulkComment}
              className="ml-1 px-3 py-1.5 qhd:px-4 qhd:py-2 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 text-sm qhd:text-base font-medium rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 active:scale-95 transition-all shadow-sm"
            >
              Add Comment
            </button>
          )}
        </OathSelectionBanner>
        <table className="min-w-full">
          <thead ref={theadRef}>
            <tr className="bg-gray-100 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-700">
              <th scope="col" className="px-4 py-4 w-10"></th>
              <th scope="col" className="px-4 py-4 text-left">
                <OathSortableHeader label="Report #" field={SORT_FIELDS.REPORT_NUMBER} sortField={sortBy} sortDescending={sortDescending} onSort={onSortChange} />
              </th>
              <th scope="col" className="px-4 py-4 text-left">
                <OathSortableHeader label="Batch ID" field={SORT_FIELDS.BATCH_ID} sortField={sortBy} sortDescending={sortDescending} onSort={onSortChange} />
              </th>
              <th scope="col" className="px-4 py-4 text-left">
                <OathSortableHeader label="Agency" field={SORT_FIELDS.AGENCY_ID} sortField={sortBy} sortDescending={sortDescending} onSort={onSortChange} />
              </th>
              <th scope="col" className="px-4 py-4 text-center">
                <OathSortableHeader label="Powers" field={SORT_FIELDS.POWER_COUNT} sortField={sortBy} sortDescending={sortDescending} onSort={onSortChange} align="center" />
              </th>
              <th scope="col" className="px-4 py-4 text-right">
                <OathSortableHeader label="Open Liability" field={SORT_FIELDS.BOND_AMOUNT} sortField={sortBy} sortDescending={sortDescending} onSort={onSortChange} align="right" />
              </th>
              <th scope="col" className="px-4 py-4 text-left">
                <OathSortableHeader label="Report Date" field={SORT_FIELDS.REPORT_DATE} sortField={sortBy} sortDescending={sortDescending} onSort={onSortChange} />
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
            {reportGroups.map((report, index) => (
              <ReportRow
                key={report.reportNo || report.reportId || index}
                report={report}
                isExpanded={expandedReports.has(report.reportNo)}
                isLoading={loadingReportPowers.has(report.reportNo)}
                powers={reportPowersCache[report.reportNo]}
                onToggle={() => onToggleExpanded(report.reportNo)}
                onLoadPowers={onLoadReportPowers}
                onViewPowerDetails={onViewPowerDetails}
                selectedPowers={selectedPowers}
                onSelectionChange={onSelectionChange}
              />
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View with Infinite Scroll */}
      <div className="relative lg:hidden bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        {/* Top accent bar */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary/60 via-primary to-primary/60 rounded-t-xl" />
        {/* Selection banner */}
        <OathSelectionBanner
          selectedCount={selectedPowers.size}
          totalCount={totalPowers}
          selectAllMatching={selectAllMatching}
          onSelectAllMatching={onSelectAllMatching}
          onClearSelection={() => onSelectAllMatching && onSelectAllMatching(false)}
        >
          {onBulkDischarge && (
            <button
              onClick={onBulkDischarge}
              className="ml-1 px-3 py-1.5 bg-primary text-white text-sm font-medium rounded-lg hover:bg-primary-dark active:scale-95 transition-all shadow-sm"
            >
              Discharge Now
            </button>
          )}
          {onAddToDischargeQueue && (
            <button
              onClick={onAddToDischargeQueue}
              className="ml-1 px-3 py-1.5 bg-white dark:bg-gray-800 text-primary dark:text-primary-light text-sm font-medium rounded-lg border border-primary/40 dark:border-primary-light/40 hover:bg-primary-50 dark:hover:bg-primary-900/30 active:scale-95 transition-all shadow-sm"
            >
              Add to Queue
            </button>
          )}
          {onBulkComment && (
            <button
              onClick={onBulkComment}
              className="ml-1 px-3 py-1.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 active:scale-95 transition-all shadow-sm"
            >
              Comment
            </button>
          )}
        </OathSelectionBanner>
        <div className="p-4 space-y-3">
        {displayReports.map((report, index) => (
          <ReportCard
            key={report.reportNo || report.reportId || index}
            report={report}
            isExpanded={expandedReports.has(report.reportNo)}
            isLoading={loadingReportPowers.has(report.reportNo)}
            powers={reportPowersCache[report.reportNo]}
            onToggle={() => onToggleExpanded(report.reportNo)}
            onLoadPowers={onLoadReportPowers}
            onViewPowerDetails={onViewPowerDetails}
            selectedPowers={selectedPowers}
            onSelectionChange={onSelectionChange}
          />
        ))}

        {/* Infinite scroll sentinel and loading indicator (mobile only) */}
        {isMobile && totalReports > 0 && (
          <>
            {/* Loading more indicator */}
            {isLoadingMore && (
              <div className="flex items-center justify-center py-4">
                <OathSpinner size="sm" />
                <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">Loading more reports...</span>
              </div>
            )}

            {/* Sentinel element for intersection observer */}
            {pageNumber < totalPages && !isLoadingMore && (
              <div ref={sentinelRef} className="h-4" aria-hidden="true" />
            )}

            {/* End of results indicator */}
            {pageNumber >= totalPages && accumulatedReports.length > 0 && (
              <div className="py-4 text-center text-sm text-gray-500 dark:text-gray-400 border-t border-gray-200 dark:border-gray-700">
                Showing all {totalReports.toLocaleString()} reports
              </div>
            )}
          </>
        )}
        </div>
      </div>

      {/* Fixed Footer Clone - appears when pagination is not visible (desktop only) */}
      {enableFixedHeaderFooter && isFooterFixed && totalReports > 0 && (
        <div style={fixedFooterStyle} className="bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 shadow-[0_-2px_10px_rgba(0,0,0,0.1)]">
          <OathPagination
            currentPage={pageNumber}
            totalPages={totalPages}
            totalCount={totalReports}
            pageSize={pageSize}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            isLoading={isLoading}
            label="reports"
          />
        </div>
      )}

      {/* Desktop Pagination (hidden on mobile) */}
      {totalReports > 0 && !isMobile && (
        <div ref={paginationRef} className="hidden lg:block border-x border-b border-gray-200 dark:border-gray-700 rounded-b-xl overflow-hidden">
          <OathPagination
            currentPage={pageNumber}
            totalPages={totalPages}
            totalCount={totalReports}
            pageSize={pageSize}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            isLoading={isLoading}
            label="reports"
          />
        </div>
      )}
    </div>
  );
};

export default GroupedResultsTable;
